import * as React from "react";
import {cx} from "emotion";

import "./ship-additional.scss";

import {getMinPrice}        from "@Common/logic/template-options/get-min-price";
import {cs}                 from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {NumberInputBinding} from "@Common/react/number-input-binding";
import {Static2}            from "@Common/react/static-2";
import {UseState}           from "@Common/react/use-state";
import {isDevQuan}          from "@Common/ui-components/debug/is-dev-quan";
import {InformationBadge}   from "@Common/ui-components/heavy/common/design2/information-badge/information-badge";
import {ErrorHighlighter}   from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}     from "@Common/ui-components/light/scroll-into-view";
import {chain}              from "@Common/utils/fs";

import {StaticTooltip} from "./static-tooltip/static-tooltip";

export const ShipAdditional = ({paperType, method, productAllowAdditionalShipped, next}) => cs(
    consumeContext("apis"),
    consumeContext("mailingLists"),
    consumeContext("cart"),
    consumeContext("company"),
    ["count", (_, next) => UseState({next})],
    ["domRef", (_, next) => Static2({next})],
    ["apiError", (_, next) => UseState({next})],
    ["errorHighlighter", (_, next) => ErrorHighlighter({next})],
    ["minShippingPrice", (_, next) => next(chain(
        paperType?.productPrice?.["shipping"],
        (_) => !_ ? null : getMinPrice(_),
    ))],
    ({company, count, apiError, domRef, errorHighlighter, apis, cart: rootCart, minShippingPrice, mailingLists}) => {
        const highlightErrors = () => {
            scrollIntoView(domRef.get());
            errorHighlighter.start();
        };
        return next({
            ... (!count.value || method !== "mailing") ? null : {
                onSaved: async ({item, cartId}) => {

                    const cloneItemJson = {
                        itemId: item.itemId,
                        additionalShip: true,
                        cloneCCState: item.proofEngineType === 0,
                        quantity: count.value,
                    };

                    const {error, itemId} = await (async () => {
                        const result = await apis.next.item.cloneItem({cloneItemJson});
                        return {error: result?.errors?.msg, itemId: result.itemId};
                    })();

                    if (isDevQuan() || error) {
                        highlightErrors();
                        apiError.onChange(error);
                        return false;
                    }

                    await apis.next.cart.addItemsToCart({cartId, itemIds: [itemId]});
                    //reload cart to ensure that all variables are correct
                    const savedCart = await apis.next.cart.validateCart(cartId);
                    rootCart.onChange(savedCart);

                    return true;
                },
                itemEditor: {
                    $name: "ship additional",
                    valid: !apiError.value && (!count.value || count.value >= minShippingPrice),
                    highlightErrors,
                },
            },

            renderRows: ({showErrors}) => !mailingLists.value?.length || !company?.prefs?.enableShippedProductWithMailOrder || !productAllowAdditionalShipped ? [] : [
                {
                    key: "ship-additional",
                    className: "ship-additional-37k",
                    color: "dark",
                    columns: [
                        {},
                        {
                            colSpan: 2,
                            className: "label",
                            render: () => <>
                                <div className="line1">
                                    <div className="text" xd-qa-type="label" xd-qa-name="Ship Additional Copies">
                                        Ship additional copies of my mailed order
                                    </div>
                                    {InformationBadge({
                                        getTooltip: () => (
                                            "Entering a quantity here will create an additional shipping order to add " +
                                            "to your cart with your mailed order when you proceed to checkout. For " +
                                            `additional shipped cards, minimum order quantity is ${minShippingPrice}. ` +
                                            "Additional shipping charge may apply."
                                        ),
                                        xdQaName: "Ship Additional Info"
                                    })}
                                </div>
                            </>,
                        },
                        {
                            className: cx("count"),
                            ref: domRef.set,
                            render: () => cs(
                                ["binding", (_, next) => NumberInputBinding({
                                    state: {
                                        value: count.value,
                                        onChange: (v) => {
                                            apiError.value && apiError.onChange(null);
                                            count.onChange(v);
                                        }
                                    },
                                    defaultValue: 0,
                                    next,
                                })],
                                ({binding}) => (
                                    <input {...{
                                        ...binding,
                                        className: cx({hasError: showErrors && (apiError.value || (count.value && count.value < minShippingPrice))}, errorHighlighter.css),
                                        "xd-qa-type": "text_field",
                                        "xd-qa-name": "Ship Additional Copies",
                                    }}/>
                                )
                            ),
                        },

                        {
                            className: "tooltip",
                            colSpan: 2,
                            render: () => {
                                return apiError.value ? (
                                    StaticTooltip({
                                        color: "red",
                                        position: "east-south",
                                        text: apiError.value,
                                        onClose: () => apiError.onChange(null),
                                    })
                                ) : (showErrors && count.value && count.value < minShippingPrice) && (
                                    StaticTooltip({
                                        color: "red",
                                        position: "east-south",
                                        text: `Minimum quantity for shipped order (${minShippingPrice}) not met.`,
                                    })
                                );
                            },
                        }
                    ],
                }
            ],
        });
    },
);
